import { useSelector } from "react-redux";
import { Trans, useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { selectSession } from "../session/selectors";
import { useConsent } from "./ConsentContext";

/**
 * Consent prompt. Shown automatically after login until a choice is made
 * (mandatory — not dismissible), and re-openable later via openPrompt() to
 * change the choice (dismissible). Wires the choice to the analytics gate.
 */
export default function ConsentPrompt() {
  const { t } = useTranslation();
  const session = useSelector(selectSession());
  const { decision, grant, deny, promptOpen, closePrompt } = useConsent();

  const isAuthenticated = Boolean(session?.token || session?.user);
  // First-time, post-login choice is mandatory; manual re-opens are dismissible.
  const mandatory = isAuthenticated && decision == null;
  const open = mandatory || promptOpen;

  const choose = (apply) => {
    apply();
    closePrompt();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !mandatory) closePrompt();
      }}
    >
      <DialogContent className="sm:max-w-md" showCloseButton={!mandatory}>
        <DialogHeader>
          <DialogTitle>{t("consent.title")}</DialogTitle>
          <DialogDescription>
            <Trans
              i18nKey="consent.description"
              components={{
                policy: (
                  <a
                    href="https://betterfield.pt/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium underline underline-offset-4"
                  >
                    {/* text comes from the translation */}
                  </a>
                ),
              }}
            />
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => choose(deny)}>
            {t("consent.decline")}
          </Button>
          <Button onClick={() => choose(grant)}>{t("consent.accept")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
